// React
import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import PropTypes from 'prop-types';
import ReactAriaModal from 'react-aria-modal';

// VDS
import Panel from '@visa/vds-react/panel-styled';
import PanelPanel from '@visa/vds-react/panel-panel-styled';

// CYBS
import CybsButton from '@cybs-ui/components/lib/button';
import { StructFlex, StructItem } from '@cybs-ui/components/lib/struct-flex';

import styles from './component.styles';

const CybsPanel = React.forwardRef(
  (
    {
      dataTest,
      escapeExits,
      i18n: i18nProp,
      isOpen,
      children,
      onClosePanel,
      customCSS,
      modalStyle,
      ...props
    },
    ref
  ) => {
    const i18n = {
      ...CybsPanel.defaultProps.i18n,
      ...i18nProp
    };

    const headerRef = React.createRef();
    useEffect(() => {
      if (isOpen) {
        // eslint-disable-next-line no-unused-expressions
        headerRef.current
          .closest('.vds-panel.vds-state--open')
          ?.removeAttribute('aria-expanded');
        headerRef.current.focus();
      }
    }, [isOpen]);

    // Logic to pause focus during calls from cybs.ajax.
    // Remove when react-aria-modal/focus-trap-react updates to use trapStack
    // Initial state determined by if pageloader is present
    const [isFocusPaused, setIsFocusPaused] = useState(Boolean(document.getElementById('modal-loader')));

    function pauseFocus() {
      setIsFocusPaused(true);
    }
    function resumeFocus() {
      setIsFocusPaused(false);
    }

    // eslint-disable-next-line no-unused-expressions
    useEffect(() => {
      // Addd event listers
      document.addEventListener('ajaxStart', pauseFocus);
      document.addEventListener('ajaxStop', resumeFocus);
      document.addEventListener('ajaxError', resumeFocus);
      return () => {
        // Cleanup
        document.removeEventListener('ajaxStart', pauseFocus);
        document.removeEventListener('ajaxStop', resumeFocus);
        document.removeEventListener('ajaxError', resumeFocus);
      };
    }, []);

    // Styles (refactor later)
    const panelStyles = theme => [styles(theme).panel, customCSS(theme)];
    const titleTextStyles = theme => styles(theme).titleText;
    const closeButtonStyles = theme => styles(theme).closeButton;
    const tooltipStyles = theme => styles(theme).tooltip;
    const panelBodyStyles = theme => styles(theme).panelBody;

    return ReactDOM.createPortal(
      <div ref={ref}>
        {isOpen && (
          <ReactAriaModal
            underlayStyle={modalStyle}
            titleId="titleIdForModal"
            onExit={onClosePanel}
            escapeExits={escapeExits}
            focusTrapPaused={isFocusPaused}
          >
            <Panel
              data-test={`${dataTest}-cybsPanel`}
              open={isOpen}
              {...props}
              css={panelStyles}
            >
              <PanelPanel>
                <StructFlex
                  align-items="flex-start"
                  justify-content="space-between"
                >
                  {i18n.title && (
                    <StructItem>
                      <h2
                        ref={headerRef}
                        tabIndex={-1}
                        css={titleTextStyles}
                        id="titleIdForModal"
                      >
                        {i18n.title}
                      </h2>
                    </StructItem>
                  )}
                  <StructItem css={closeButtonStyles}>
                    <CybsButton
                      i18n={{
                        ariaLabel: i18n.ariaLabelClose,
                        tooltipLabel: i18n.ariaLabelClose
                      }}
                      variant="icon"
                      dataTest={`${dataTest}-closePanel`}
                      iconClass="close"
                      iconType="light-tiny"
                      resolution="tiny"
                      colorScheme="secondary"
                      onClick={onClosePanel}
                      tooltipCustomCSS={tooltipStyles}
                    />
                  </StructItem>
                </StructFlex>
                <div css={panelBodyStyles}>{children}</div>
              </PanelPanel>
            </Panel>
          </ReactAriaModal>
        )}
      </div>,
      document.querySelector('body')
    );
  }
);

CybsPanel.propTypes = {
  /**
   * Content
   */
  children: PropTypes.node.isRequired,
  /**
   * dataTest string for testing.
   */
  dataTest: PropTypes.string.isRequired,
  /**
   * By default, the Escape key exits the modal.
   */
  escapeExits: PropTypes.bool,
  /**
   * Internationalization values
   */
  i18n: PropTypes.shape({
    title: PropTypes.string,
    ariaLabelClose: PropTypes.string
  }),
  /**
   * indicates if the panel is open or not
   */
  isOpen: PropTypes.bool,
  /**
   * Callback fired on escape or close click
   */
  onClosePanel: PropTypes.func,

  /**
   * Styling function to allow CSS overrides via Emotion
   *
   * @ignore
   */
  customCSS: PropTypes.func,

  /**
   * Custom object styles for the underlayStyle prop from react-aria-modal
   *
   * @ignore
   */
  modalStyle: PropTypes.shape({})
};

CybsPanel.defaultProps = {
  escapeExits: true,
  i18n: {
    title: undefined,
    ariaLabelClose: 'Close Panel'
  },
  isOpen: false,
  onClosePanel: () => {},
  customCSS: () => '',
  modalStyle: { zIndex: 99998 }
};

CybsPanel.displayName = 'CybsPanel';

export default CybsPanel;
